// Streaming configuration
const STREAM_CONFIG = {
  TIMEOUT_MS: 300000, // 5 minutes
  MAX_RETRIES: 3,
  BASE_DELAY_MS: 1000, // 1 second base delay for exponential backoff
  SCROLL_THRESHOLD_PX: 50, // Consider user at bottom if within 50px
  SCROLL_DEBOUNCE_MS: 100,
  AUTO_SCROLL_DELAY_MS: 100,
} as const;

// Message display configuration
const MESSAGE_CONFIG = {
  MAX_WIDTH_PERCENTAGE: 85, // Max width for message bubbles
  TOOL_MAX_WIDTH_PERCENTAGE: 75, // Max width for tool messages
  AVATAR_SIZE: 32, // Avatar size in pixels (w-8 h-8)
  ANIMATION_DURATION_MS: 300,
} as const;

// SSE Event types
const SSE_EVENTS = {
  SESSION: 'session',
  TOOLS_START: 'tools-start',
  TOOL_EXECUTING: 'tool-executing',
  TOOL_COMPLETED: 'tool-completed',
  TOOLS_COMPLETE: 'tools-complete',
  ASSISTANT_CONTENT: 'assistant-content',
  CONVERSATION_COMPLETE: 'conversation-complete',
  ERROR: 'error',
} as const;

// Tool status types
export const TOOL_STATUS = {
  CALLING: 'calling',
  EXECUTING: 'executing',
  COMPLETED: 'completed',
  ERROR: 'error',
  PENDING: 'pending',
} as const;

// Message roles
export const MESSAGE_ROLES = {
  USER: 'user',
  ASSISTANT: 'assistant',
  TOOL: 'tool',
} as const;

// CSS class constants for consistent styling
export const CHAT_STYLES = {
  AVATAR: {
    BASE: 'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm',
    USER: 'bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 border border-primary-300',
    ASSISTANT: 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700',
    ASSISTANT_WHITE: 'bg-gradient-to-br from-white to-gray-50 text-gray-700 border border-gray-200',
    TOOL: 'bg-gradient-to-br from-green-100 to-green-200 text-green-700',
    ERROR: 'bg-gradient-to-br from-red-100 to-red-200 text-red-700 border border-red-300',
    LOADING: 'bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700',
  },
  BUBBLE: {
    BASE: 'rounded-2xl px-4 py-3 shadow-sm break-words overflow-wrap-anywhere',
    USER: 'bg-primary-600 text-white rounded-br-md',
    ASSISTANT: 'bg-white border border-gray-200 text-gray-800 rounded-bl-md',
    TOOL: 'bg-white border border-green-200 rounded-bl-md',
    TOOL_COMPACT: 'bg-white border border-green-200 rounded-2xl rounded-bl-md px-3 py-2',
    TOOL_ACTIVE: 'bg-white border border-primary-200 rounded-2xl rounded-bl-md px-3 py-2',
    STREAMING: 'bg-white border border-gray-200 rounded-bl-md relative overflow-hidden',
    ERROR: 'bg-red-50 border border-red-200 rounded-bl-md',
  },
  HEADER: {
    BASE: 'flex items-center gap-2 text-xs mb-1',
    USER: 'text-primary-100 justify-end',
    ASSISTANT: 'text-gray-500 justify-start',
    TOOL: 'text-gray-500 justify-start',
  },
} as const;

// Default chat suggestions for empty state
const DEFAULT_SUGGESTIONS = [
  {
    text: 'Analyze channel performance',
    iconType: 'chart',
  },
  {
    text: 'Track visitor journeys',
    iconType: 'journey',
  },
  {
    text: 'Email impact analysis',
    iconType: 'email',
  },
  {
    text: 'Campaign ROI insights',
    iconType: 'roi',
  },
] as const;

// Error messages
const ERROR_MESSAGES = {
  STREAM_TIMEOUT: 'Connection timed out. Please try again.',
  STREAM_CANCELLED: 'Stream cancelled',
  CONVERSATION_CREATE_FAILED: 'Failed to create conversation',
  NO_CONVERSATION: 'No conversation available',
  UNKNOWN_STREAMING_ERROR: 'Unknown streaming error',
  CRITICAL_ERROR: 'Critical error occurred',
  RESPONSE_NOT_OK: (status: number) => `HTTP error! status: ${status}`,
  RESPONSE_NOT_READABLE: 'Response body is not readable',
} as const;

// Development environment checks
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// Regular expressions for content processing
export const CONTENT_REGEX = {
  SYSTEM_TAGS: /<system[^>]*>[\s\S]*?<\/system>/gi,
  LANGUAGE_CODE: /language-(\w+)/,
  TOOL_SUCCESS: /"success":true/,
} as const;

// Animation classes
const ANIMATIONS = {
  BOUNCE: {
    FIRST: 'animate-bounce [animation-delay:-0.3s]',
    SECOND: 'animate-bounce [animation-delay:-0.15s]',
    THIRD: 'animate-bounce',
  },
  FADE_IN: 'animate-in fade-in slide-in-from-bottom-2 duration-300',
  PULSE: 'animate-pulse',
  PING: 'animate-ping',
  SPIN: 'animate-spin',
} as const;

// Empty state configuration
const EMPTY_STATE = {
  TITLE: 'Welcome to marketbetter AI Chat',
  SUBTITLE: "Share your website URL and I'll help you optimize your marketing strategy",
} as const;

// Markdown wrapper styles — used by MarkdownRenderer
export const MARKDOWN_STYLES = {
  PROSE: 'prose max-w-none break-words text-[14px]',
  PROSE_WHITE: 'prose max-w-none text-white break-words text-[14px]',
} as const;
